import { Component, Vue, Prop, Emit } from 'vue-property-decorator';
import { Icon, Alert, Spinner } from '../../components';

@Component
export default class TextInput extends Vue {
  @Prop() className!: string;
  @Prop() id!: string;
  @Prop() parent!: string;
  @Prop() value!: string;
  @Prop() name!: string;
  @Prop() disabled!: boolean;
  @Prop() maxLength!: number;
  @Prop() minLength!: number;
  @Prop() label!: string;
  @Prop() placeholder!: string;
  @Prop() inputTextboxLocator!: string;
  @Prop() description!: string;
  @Prop() loading!: boolean;
  @Prop() crossIcon!: boolean;
  @Prop() searchIcon!: boolean;
  @Prop() iconOnRightSide!: boolean;
  @Prop() iconName!: string;
  @Prop() required!: boolean;
  @Prop() tabIndex!: number;
  @Prop() isDropdown!: boolean;
  @Prop() readonly!: boolean;
  @Prop() type!: string;
  @Prop() errors!: string[];
  @Prop() assistiveText!: string;
  @Prop() successMessage!: string;
  @Prop() autofocus!: boolean;
  @Prop() highlightIcon!: boolean;

  // State
  showDescription: boolean = true;
  // value: string = '';
  labelUp: boolean = false;
  isDown: boolean = true;
  labelFocus: string = '';

  mounted(): void {
    let labelElems = document.getElementsByClassName('text-input-small');
    for (const elem in labelElems) {
      if (labelElems[elem] && labelElems[elem].clientHeight > 20) {
        labelElems[elem].classList.add('text-input-large');
        labelElems[elem].classList.remove('text-input-small');
      }
    }

    labelElems = document.getElementsByClassName('text-input-small-focus');
    for (const elem in labelElems) {
      if (labelElems[elem] && labelElems[elem].clientHeight > 20) {
        labelElems[elem].classList.add('text-input-large-focus');
        labelElems[elem].classList.remove('text-input-small-focus');
      }
    }
  }

  getAssistiveText(): JSX.Element[] | JSX.Element | string {
    let content: JSX.Element[] | JSX.Element | string;
    if (this.errors) {
      content = this.errors.map((err, index) =>
        <Alert key={`err-${index}`}
          name='alert-inline-warning'
          icon='icon-system-alert-small'
          label={err}
          labelClass='error-message body14-light' />,
      );
    } else if (this.successMessage) {
      content = <Alert name='alert-inline-success' icon='icon-system-tick-copy' label={this.successMessage} labelClass='success-message body14-light' />;
    } else if (this.assistiveText && this.showDescription) {
      content = <Alert name='alert-inline-info-tick' label={this.assistiveText} labelClass='body14-light' />;
    } else {
      content = '';
    }

    return (
      content ? <div className='tl-text-input-assistive-text'>
        <div className='subtext-placeholder' id='subtext-placeholder'>{content}</div>
      </div> : ''
    );
  }

  getInputClass(propClass: string): string {
    let inputClass: string = 'tl-text-input-box ';
    if (this.errors) {
      inputClass += 'input-error ';
    } else if (this.successMessage) {
      inputClass += 'input-success ';
    }
    if (propClass) {
      inputClass += propClass;
    }
    return inputClass;
  }

  renderInputBox(): JSX.Element[] | JSX.Element | string {
    const labelClass: string = 'heading16';
    let labelClassFocus: string;

    if (this.labelFocus === '' || this.value || this.placeholder) {
      if ((this.value && this.value.length > 0) || this.placeholder) {
        labelClassFocus = 'text-input-small-focus';
      } else {
        labelClassFocus = 'text-input-small';
      }
    } else {
      labelClassFocus = this.labelFocus;
    }

    const labelUp = labelClass + ' ' + labelClassFocus;
    const inputClass: string = this.getInputClass(this.className);
    const labelId: string = this.id ? this.id + '-label' : this.name + '-label';
    return (
      <div className='tl-input'>
        {
          <input
            className={inputClass}
            id={this.id}
            name={this.name}
            type={this.type ? this.type : 'text'}
            placeholder={this.placeholder || ''}
            value={this.value}
            maxLength={this.maxLength}
            minLength={this.minLength}
            required={this.required}
            tabIndex={this.tabIndex}
            readOnly={this.readonly}
            onKeyPress={this.onKeyPress}
            onKeyUp={this.onKeyUp}
            onKeyDown={this.onKeyDown}
            onFocus={this.onFocus}
            onBlur={this.onBlur}
            onChange={this.handleChange}
            onClick={this.onClick}
            autoFocus={this.autofocus}
            disabled={this.disabled}
            aria-label='text-input'
            aria-required='true'
          />
        }
        {
          this.disabled || !this.label ?
            <div className={`label-disabled ${labelUp}`}>{this.label}</div> :
            <label id={labelId} className={labelUp} form={this.parent} htmlFor={this.id} >
              {this.label}
            </label>
        }
        {this.loading && <div className='text-spinner'><Spinner size='tl-spinner-small' /></div>}

        {this.crossIcon && !this.loading &&
          <div aria-label='crossIcon' onClick={this.onCrossIconClick}
            onKeyDown={(e: any) => this.onCrossKeyPressed(e)}>
            <svg tabIndex={0} className={`${this.searchIcon ? 'icon-system-ui-cross svg-default-style icon-up-down tl-input-cross-icon'
              : 'icon-system-ui-cross svg-default-style icon-up-down tl-input-single-cross-icon'}`}>
              <Icon name='icon-system-ui-cross' /></svg>
          </div>}

        {this.searchIcon && !this.loading &&
          <div aria-label='searchIcon' onClick={this.onSearchIconClick}
            onKeyDown={(e: any) => this.onSearchKeyPressed(e)}>
            <svg tabIndex={0} className={`${!this.highlightIcon ? 'icon-system-ui-search svg-default-style icon-up-down tl-input-search-icon'
              : 'icon-system-ui-search svg-default-style icon-up-down tl-input-search-icon highlight-icon'}`}>
              <Icon name='icon-system-search' /></svg> </div>
        }

        {
          this.isDropdown && this.isDown &&
          <span onClick={this.onDownIconClick}>
            <span>
              <svg className='icon-system-ui-down-arrow svg-default-style icon-up-down'>
                <Icon name='icon-system-ui-down-arrow' />
              </svg>
            </span>
          </span>
        }
        {
          this.isDropdown && !this.isDown &&
          <svg className='icon-system-ui-up-arrow svg-default-style icon-up-down'>
            <Icon name='icon-system-ui-up-arrow' />
          </svg>
        }
        {
          this.iconOnRightSide && this.iconName &&
          <svg className='icon-system-ui-up-arrow svg-default-style icon-up-down tl-input-right-icon' onClick={this.onIconClick}>
            <Icon name={this.iconName} />
          </svg>
        }
      </div>
    );
  }

  @Emit()
  onFocus(event: any): void {
    this.$emit('focus', event);

    let labelHeight = 20;
    if (event.target && event.target.nextSibling) {
      labelHeight = event.target.nextSibling.clientHeight;
    }

    this.labelFocus = labelHeight > 20 ? 'text-input-large-focus' : 'text-input-small-focus';
    this.showDescription = true;
    this.isDown = !this.isDown;
  }

  @Emit()
  onBlur(event: any): void {
    this.$emit('blur', event);

    let labelHeight: number = 20;
    if (event.target && event.target.nextSibling) {
      labelHeight = event.target.nextSibling.clientHeight;
    }
    let labelFocus: string;
    if (event.target.value && event.target.value.length > 0) {
      labelFocus = labelHeight > 20 ? 'text-input-large-focus' : 'text-input-small-focus';
    } else {
      labelFocus = labelHeight > 20 ? 'text-input-large' : 'text-input-small';
    }

    this.labelUp = event.target.value && event.target.value.length > 0;
    this.labelFocus = labelFocus;
    this.isDown = !this.isDown;
  }

  render(): JSX.Element {
    return (
      <div className='tl-text-input tl-ui'>
        {this.renderInputBox()}
        {this.getAssistiveText()}
      </div>
    );
  }

  @Emit('onChange')
  handleChange(event: any): void {
    this.$emit('change', event);
  }

  @Emit()
  onIconClick(event: any): void {
    this.$emit('iconClick', event);
  }

  @Emit()
  onSearchKeyPressed(event: any): void {
    if (event.keyCode === 13) {
      this.$emit('searchIconClick', event);
    }
  }

  @Emit()
  onCrossKeyPressed(event: any): void {
    if (event.keyCode === 13) {
      this.$emit('crossKeyPressed', event);
    }
  }

  @Emit()
  onKeyPress(event: any): void {
    this.$emit('keyPress', event);
  }

  @Emit()
  onKeyUp(event: any): void {
    this.$emit('keyUp', event);
  }

  @Emit()
  onKeyDown(event: any): void {
    this.$emit('keyDown', event);
  }

  @Emit()
  onClick(event: any): void {
    this.$emit('click', event);
  }

  @Emit()
  onCrossIconClick(event: any): void {
    this.$emit('crossIconClick', event);
  }

  @Emit()
  onDownIconClick(event: any): void {
    this.$emit('downIconClick', event);
  }

  @Emit()
  onSearchIconClick(event: any): void {
    this.$emit('searchIconClick', event);
  }
}
